import { useState } from 'react'
import type { PortalUser } from '../../api/types'
import { useT } from '../../i18n'
import { Button, Modal, TextField } from '../../ui'
import screen from '../screen.module.css'

export interface UserFormValues {
  readonly email: string
  readonly first_name: string
  readonly last_name: string
}

export interface UserFormModalProps {
  readonly open: boolean
  /** Present ⇒ edit; absent ⇒ create. Editing cannot change the e-mail,
   *  because it is the Keycloak username and the account identity. */
  readonly user: PortalUser | null
  readonly busy: boolean
  readonly onSubmit: (values: UserFormValues) => void
  readonly onClose: () => void
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const EMPTY: UserFormValues = { email: '', first_name: '', last_name: '' }

export function UserFormModal({
  open, user, busy, onSubmit, onClose,
}: UserFormModalProps) {
  const t = useT()
  // Initialised once per mount, with no re-seeding effect: the caller gives
  // this component a `key` derived from the user being edited, so switching
  // from one user to another (or from edit to create) is a fresh mount with
  // fresh state. That is React's own answer to "reset state when a prop
  // changes", and it avoids the extra render an effect-based reset costs.
  const [values, setValues] = useState<UserFormValues>(() =>
    user
      ? {
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
        }
      : EMPTY,
  )
  const [touched, setTouched] = useState(false)

  const editing = user !== null
  const emailError = !editing && touched && !EMAIL_RE.test(values.email) ? t('users.emailInvalid') : undefined
  const firstError = touched && values.first_name.trim() === '' ? t('users.required') : undefined
  const lastError = touched && values.last_name.trim() === '' ? t('users.required') : undefined
  const valid = !emailError && !firstError && !lastError &&
    values.first_name.trim() !== '' && values.last_name.trim() !== '' &&
    (editing || EMAIL_RE.test(values.email))

  const submit = () => {
    setTouched(true)
    if (!valid) return
    onSubmit({
      ...values,
      first_name: values.first_name.trim(),
      last_name: values.last_name.trim(),
      email: values.email.trim(),
    })
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? t('users.editTitle') : t('users.createTitle')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} loading={busy}>
            {editing ? t('users.save') : t('users.createAndInvite')}
          </Button>
        </>
      }
    >
      <form
        className={screen.form}
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <TextField
          label={t('users.email')}
          type="email"
          value={values.email}
          required={!editing}
          disabled={editing}
          {...(editing ? { description: t('users.emailImmutable') } : {})}
          {...(emailError ? { error: emailError } : {})}
          onChange={(e) => setValues((v) => ({ ...v, email: e.target.value }))}
        />
        <TextField
          label={t('users.firstName')}
          value={values.first_name}
          required
          {...(firstError ? { error: firstError } : {})}
          onChange={(e) => setValues((v) => ({ ...v, first_name: e.target.value }))}
        />
        <TextField
          label={t('users.lastName')}
          value={values.last_name}
          required
          {...(lastError ? { error: lastError } : {})}
          onChange={(e) => setValues((v) => ({ ...v, last_name: e.target.value }))}
        />
        {/* No role control: every portal user of a biller is a biller user
            and there is nothing else they could be. A select with one option
            is a worse answer than no select. */}
        {/* Submits on Enter from any field without adding a visible button
            that duplicates the footer's. */}
        <button type="submit" className="sr-only" tabIndex={-1} aria-hidden="true">
          {t('users.save')}
        </button>
      </form>
    </Modal>
  )
}
